import { formatDistanceToNow } from 'date-fns';

/**
 * Rework Queue Item - v2.1
 *
 * Displays a rework task in the work center queue.
 * Shows issue type, severity, project info, and actions.
 *
 * Props:
 * - item: Rework queue item from API
 * - onStart: () => void - Start working on rework
 * - onView: () => void - View details
 */
export default function ReworkQueueItem({ item, onStart, onView }) {
  const severityColors = {
    scrap: 'bg-red-100 border-red-300 text-red-800',
    requires_rework: 'bg-amber-50 border-amber-200 text-amber-900',
    cosmetic: 'bg-gray-50 border-gray-200 text-gray-700',
  };

  const severityBadgeColors = {
    scrap: 'bg-red-500 text-white',
    requires_rework: 'bg-amber-500 text-white',
    cosmetic: 'bg-gray-400 text-white',
  };

  const colorClass = severityColors[item.severity] || severityColors.requires_rework;
  const badgeClass = severityBadgeColors[item.severity] || severityBadgeColors.requires_rework;

  // Format age
  const ageText = item.flaggedAt
    ? formatDistanceToNow(new Date(item.flaggedAt), { addSuffix: true })
    : `${parseFloat(item.ageHours || 0).toFixed(1)}h ago`;

  return (
    <div className={`rounded-lg border-2 p-4 ${colorClass}`}>
      {/* Header Row */}
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="text-lg">🔧</span>
          <span className={`px-2 py-0.5 rounded text-xs font-bold uppercase ${badgeClass}`}>
            REWORK
          </span>
          {item.severity === 'scrap' && (
            <span className="px-2 py-0.5 rounded text-xs font-bold uppercase bg-red-600 text-white">
              SCRAP
            </span>
          )}
        </div>
        <span className="text-sm text-gray-500">{ageText}</span>
      </div>

      {/* Job Info */}
      <div className="mb-3">
        <div className="font-semibold text-gray-900">
          {item.jobNumber}
        </div>
        {item.projectName && (
          <div className="text-sm text-gray-600 truncate">
            {item.projectName}
          </div>
        )}
      </div>

      {/* Issue Details */}
      <div className="text-sm text-gray-600 mb-3">
        <span className="font-medium">{item.issueLabel || item.issueType}</span>
        {item.discoveredAt && (
          <span className="mx-2">|</span>
        )}
        {item.discoveredAt && (
          <span>Found at {item.discoveredAt}</span>
        )}
        {item.reportedBy && (
          <>
            <span className="mx-2">|</span>
            <span>Reported by {item.reportedBy}</span>
          </>
        )}
      </div>

      {/* Description if available */}
      {item.description && (
        <div className="text-sm text-gray-500 mb-3 line-clamp-2">
          {item.description}
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-2 justify-end">
        {onView && (
          <button
            onClick={onView}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            View
          </button>
        )}
        {onStart && (
          <button
            onClick={onStart}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Start
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Inline Rework Warning - v2.1
 *
 * Shows a warning banner on in-progress items that have pending rework.
 * Used when rework was flagged while work was already in progress.
 *
 * Props:
 * - warning: Warning object with issue info
 * - onView: () => void - View rework details
 */
export function ReworkInlineWarning({ warning, onView }) {
  return (
    <div className="flex items-center gap-2 p-2 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
      <span className="text-amber-500">⚠️</span>
      <span className="flex-1">
        <strong>REWORK:</strong> {warning.issueLabel || warning.issueType} found at {warning.discoveredAt}
        <span className="mx-1">—</span>
        <em>fix before completing</em>
      </span>
      {onView && (
        <button
          onClick={onView}
          className="px-2 py-1 text-xs font-medium text-amber-700 hover:text-amber-900 hover:underline"
        >
          View
        </button>
      )}
    </div>
  );
}
